/**
 * Copyright © 2026 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import { useMsal } from "@azure/msal-react";
import {
  generateTrackingId,
  type V1GetAccountsCustomerAccountDto,
  type V1GetAccountsGetAccountsResponse,
} from "@farmlands/myfarmlands-api";
import { getMinutesMs } from "@farmlands/utilities";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { useSelector } from "react-redux";

import { myFarmlandsApi } from "../../../services/myfarmlands-api";
import { selectCurrentAccountNumber } from "../../azure-b2c/storage";

export type UseSelectedAccountQueryResult = {
  /**
   * The selected farmer account from `/accounts` when it matches the current selection;
   * `null` when the selection is not a MyFarmlands (shareholder) account.
   */
  selectedAccount: V1GetAccountsCustomerAccountDto | null;
  /**
   * An account available to the user from `/accounts`, independent of the selection (the
   * first entry). For user-scoped data. `null` only when the user has no MyFarmlands API
   * accounts.
   */
  availableAccount: V1GetAccountsCustomerAccountDto | null;
  /** B2C `localAccountId` of the active MSAL account. `null` if MSAL hasn't initialised. */
  accountId: string | null;
  /** True while the `/accounts` request is in flight (and the query is enabled). */
  isLoading: boolean;
  /** Error from the `/accounts` request, if any. */
  error: unknown;
  /** Re-fires the `/accounts` request. Wire to retry UI on error. */
  refetch: () => void;
};

export const useSelectedAccountQuery = (): UseSelectedAccountQueryResult => {
  const { instance } = useMsal();
  const activeAccount = instance.getActiveAccount();
  const accountId = activeAccount?.localAccountId ?? null;

  const accountNumber = useSelector(selectCurrentAccountNumber) ?? null;

  const { data, isLoading, error, refetch } = useQuery<V1GetAccountsGetAccountsResponse>({
    queryKey: ["accounts", accountId],
    queryFn: async () => {
      const response = await myFarmlandsApi.accounts.getAccounts({
        trackingID: generateTrackingId(),
      });
      return response.data;
    },
    enabled: !!accountId,
    // Keyed on the stable B2C id and rarely changes within a session; caching lets the
    // multiple consumers share one result instead of refetching on each mount/refocus.
    staleTime: getMinutesMs(5),
    gcTime: getMinutesMs(10),
  });

  const { selectedAccount, availableAccount } = useMemo(() => {
    const customerAccounts = data?.customerAccounts ?? [];
    const matchedAccount = accountNumber
      ? (customerAccounts.find((a) => a.accountNumber === accountNumber) ?? null)
      : null;
    const firstAccount = customerAccounts[0] ?? null;

    return {
      selectedAccount: matchedAccount,
      availableAccount: firstAccount,
    };
  }, [data, accountNumber]);

  return {
    selectedAccount,
    availableAccount,
    accountId,
    isLoading,
    error,
    refetch: () => void refetch(),
  };
};
